import { Component, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { Step } from 'src/app/models/invoice.model';
import { DatosFacturaCompleta } from 'src/app/models/carga-fc.dto';
import { ComproCabeProveService } from 'src/app/services/compro-cabe-prove.service';
import { errorMessage, errorMessageHandler } from 'src/app/core/utils/error-handler.utils';
import { alertConfirmHTML } from 'src/app/core/utils/message-handler.util';
import { Subject, takeUntil } from 'rxjs';
import { DatosFacturasProveedorComponent } from './componentes/datos-facturas-proveedor/datos-facturas-proveedor.component';
import { TotalesFacturasProveedorComponent } from './componentes/totales-facturas-proveedor/totales-facturas-proveedor.component';

@Component({
  selector: 'app-nueva-factura',
  templateUrl: './nueva-factura.component.html',
  styleUrls: ['./nueva-factura.component.scss']
})
export class NuevaFacturaComponent implements OnInit {

  currentStep = 1;
  steps: Step[] = [
    { id: 1, name: 'Datos' },
    { id: 2, name: 'Conceptos' },
    { id: 3, name: 'Totales' },
  ];
  miniLoader: boolean = false;

  datosFactura: DatosFacturaCompleta | null = null;
  private destroy$ = new Subject<void>();

  @ViewChild(DatosFacturasProveedorComponent)
  private datosFacturaComponent!: DatosFacturasProveedorComponent;

  @ViewChild(TotalesFacturasProveedorComponent)
  private totalesFacturasComponent!: TotalesFacturasProveedorComponent;


  constructor(private router: Router,
    private comproCabeProveService: ComproCabeProveService
  ) { }

  ngOnInit(): void {
    this.comproCabeProveService.getDatosFactura$()
      .pipe(takeUntil(this.destroy$))
      .subscribe(datos => {
        if (!datos) {
          this.router.navigate(['home/proveedores/carga-fc']);
          return;
        }
        this.datosFactura = { ...datos };
      });
  }

  nextStep(): void {

    let esPasoValido = true;

    if (this.currentStep === 1) {
      esPasoValido = this.datosFacturaComponent.validarFormulario();
    }

    if (esPasoValido && this.currentStep < this.steps.length) {
      this.currentStep++;
    }

  }

  prevStep(): void {
    if (this.currentStep > 1) this.currentStep--;
  }

  confimarCancelar(): void {

    let mensaje = `&iquest;Est&aacute; seguro de <b>cancelar</b> la operaci&oacute;n?`;

    alertConfirmHTML("question", "", mensaje, true, "Si", "No")
      .then((confirmed) => {
        if (confirmed) {
          this.cancelarOperacion();
        }
      })
      .catch((error) => {
        console.error("Error: ", error);
      });

  }

  cancelarOperacion(): void {
    this.comproCabeProveService.clearDatosFactura();
    this.router.navigate(['home/proveedores/carga-fc']);
  }

  irAlPaso(paso: number): void {
    this.currentStep = paso;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  confirmarGrabarComprobante() {

    let mensaje = `&iquest;Est&aacute; seguro de <b>confirmar</b> el alta de factura?`;

    alertConfirmHTML("question", "", mensaje, true, "Si", "No")
      .then((confirmed) => {
        if (confirmed) {
          this.grabarComprobante();
        }
      })
      .catch((error) => {
        console.error("Error: ", error);
      });
  }

  grabarComprobante() {

    if (!this.validar()) {
      return;
    }

    this.activarLoader();
    this.comproCabeProveService
      .grabarComprobante(this.datosFactura)
      .subscribe({
        next: async (response: { mensaje: string; }) => {
          if (response.mensaje) {
            this.enviarMail();

            const confirmado = await alertConfirmHTML(
              'success', // icon
              'Comprobante generado',
              response.mensaje,
              false,
              'Aceptar',
              ''
            );

            if (confirmado) {
              this.router.navigate(['/home/proveedores/carga-fc']);
            }

          } else {
            errorMessage("Error desconocido, por favor intente de nuevo");
          }
        },
        error: (error: any) => {
          errorMessageHandler(error);
          this.desactivarLoader();
        },
        complete: () => {
          this.desactivarLoader();
        },
      });
  }


  enviarMail() {
    if (!this.datosFactura || !this.datosFactura.formulario) {
      console.error('No hay datos de factura para enviar');
      return;
    }

    const formData = new FormData();

    try {
      // Validamos que tenemos los datos básicos necesarios
      const formulario = this.datosFactura.formulario;

      // Adjuntamos los datos del formulario como JSON string
      const formularioJson = JSON.stringify({
        datosBasicos: formulario.datosBasicos,
        fechas: formulario.fechas,
        totales: formulario.totales
      });

      formData.append('formulario', formularioJson);

      // Si hay archivo, validamos y lo adjuntamos
      if (formulario.archivo?.binario && formulario.archivo.binario instanceof File) {
        // Validamos el tamaño del archivo si hay un límite definido

        const nombreArchivo = formulario.archivo.nombre ||
          formulario.archivo.binario.name ||
          'factura.pdf';

        formData.append('archivo', formulario.archivo.binario, nombreArchivo);
      }

      // Opcional: agregar información adicional del comprobante si es necesaria
      if (formulario.datosBasicos?.numero) {
        formData.append('numeroComprobante', formulario.datosBasicos.numero.toString());
      }

      if (formulario.datosBasicos?.tipo) {
        formData.append('tipoComprobante', formulario.datosBasicos.tipo);
      }

      this.comproCabeProveService.enviarMailFactura(formData).subscribe({
        next: (response: any) => {
          console.info('Mail enviado correctamente:', response);
        },
        error: (err: any) => {
          console.error('Error al enviar el mail:', err);
        }
      });

    } catch (error) {
      console.error('Error al preparar los datos para envío de mail:', error);
    }
  }


  validar(): boolean {

    let esValido = true;

    if (this.totalesFacturasComponent) {
      esValido = this.totalesFacturasComponent.validarFormulario() ?? true;
    }

    return esValido;
  }


  activarLoader(): void {
    this.miniLoader = true;
  }

  desactivarLoader(): void {
    setTimeout(() => {
      this.miniLoader = false;
    }, 1000);
  }


}
